import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { useT } from '@/i18n';
import { cn } from '@/shared/lib/utils';
import { NetxolMark } from './NetxolLogo';

const initials = (name?: string | null) => (name ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '·';

/** The ISP's square logo; without one, its initials on its brand colour (Netxol blue → violet when none is set). */
export function IspLogo({ name, logoUrl, color, className }: { name?: string | null; logoUrl?: string | null; color?: string | null; className?: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [logoUrl]);
  if (logoUrl && !broken) return <img src={logoUrl} alt={name ?? ''} onError={() => setBroken(true)} className={cn('size-9 shrink-0 rounded-xl border bg-white object-contain', className)} />;
  return (
    <div className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl text-[13px] font-bold text-white shadow-sm', className)}
      style={{ background: color ?? 'linear-gradient(135deg, #2563eb, #7c3aed)' }}>{initials(name)}</div>
  );
}

/** Sidebar top: the ISP's logo and name — the ISP's brand comes first in its own console. */
export function IspBrandHeader({ tenant, compact, badge }: { tenant?: { name?: string; logo_url?: string | null; brand_color?: string | null } | null; compact?: boolean; badge?: string | null }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <IspLogo name={tenant?.name} logoUrl={tenant?.logo_url} color={tenant?.brand_color} />
      {!compact && (
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[14.5px] font-bold tracking-tight">{tenant?.name ?? 'Netxol One'}</div>
          {badge && <span className="mt-0.5 inline-block rounded bg-amber-500/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">{badge}</span>}
        </div>
      )}
    </div>
  );
}

/** Sidebar bottom: a small, muted "Powered by Netxol" (only the mark when the sidebar is collapsed). */
export function PoweredByNetxol({ compact, version }: { compact?: boolean; version?: string }) {
  const t = useT();
  return (
    <a href="https://netxol.com" target="_blank" rel="noopener noreferrer" title={t('brand.powered')}
      className={cn('flex items-center gap-1.5 px-5 py-2 text-[10.5px] text-muted-foreground/80 transition-colors hover:text-foreground', compact && 'justify-center px-0')}>
      {!compact && <span>{t('brand.powered_by')}</span>}<NetxolMark className="h-3 opacity-80" />{!compact && <span className="font-semibold">Netxol</span>}
      {version && !compact && <span className="ms-auto font-mono text-[10px] opacity-80" data-testid="netxol-version">{version}</span>}
    </a>
  );
}

/**
 * Browser tab: "<ISP name> · Netxol One", and the ISP's favicon when it has a logo — made from the logo by Core
 * (lib/brand-images.ts, the same code that makes the app icons): /brand/<isp>/favicon-<n>.png.
 */
export function useBrandHead(name?: string | null, logoUrl?: string | null) {
  useEffect(() => {
    if (!name) return;
    const prevTitle = document.title;
    document.title = `${name} · Netxol One`;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const prevIcon = link?.href; const prevSizes = link?.getAttribute('sizes');
    const favicon = logoUrl ? logoUrl.replace(/\/brand\/([^/]+)\/logo(\?.*)?$/, '/brand/$1/favicon-48.png$2') : null;
    if (link && favicon) { link.href = favicon; link.setAttribute('sizes', '48x48'); }
    let touch: HTMLLinkElement | null = null;
    if (favicon && favicon !== logoUrl) {
      touch = document.createElement('link'); touch.rel = 'apple-touch-icon'; touch.href = favicon.replace('favicon-48', 'favicon-180');
      document.head.appendChild(touch);
    }
    return () => {
      document.title = prevTitle; touch?.remove();
      if (link && prevIcon) { link.href = prevIcon; if (prevSizes) link.setAttribute('sizes', prevSizes); }
    };
  }, [name, logoUrl]);
}

/**
 * Logo picker: PNG, JPG or SVG up to 1 MB, previewed and cropped to a centred square (256 px PNG) in the browser —
 * so the server, the PDFs and the portal always get the same small square image.
 */
export function LogoPicker({ value, onChange, name, color, required }: { value: string | null; onChange: (dataUrl: string | null) => void; name?: string; color?: string | null; required?: boolean }) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const pick = (f?: File) => {
    setErr(null);
    if (!f) return;
    if (!/^image\/(png|jpeg|svg\+xml)$/.test(f.type)) { setErr(t('brand.logo_type')); return; }
    if (f.size > 1024 * 1024) { setErr(t('brand.logo_size')); return; }
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      const S = 256; const c = document.createElement('canvas'); c.width = S; c.height = S;
      const g = c.getContext('2d')!;
      const w = img.naturalWidth || S; const h = img.naturalHeight || S;
      // the whole logo fits in the square (letterboxed on white) — nothing of a wide logo is cut away
      const k = Math.min(S / w, S / h); const dw = w * k; const dh = h * k;
      g.fillStyle = '#ffffff'; g.fillRect(0, 0, S, S);
      g.drawImage(img, (S - dw) / 2, (S - dh) / 2, dw, dh);
      URL.revokeObjectURL(url);
      onChange(c.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); setErr(t('brand.logo_type')); };
    img.src = url;
  };
  return (
    <div className="flex items-center gap-3">
      <IspLogo name={name} logoUrl={value} color={color} className="size-16 rounded-2xl text-lg" />
      <div className="space-y-1">
        <div className="flex gap-2">
          <button type="button" onClick={() => input.current?.click()} className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted">
            <ImagePlus className="size-3.5" /> {value ? t('brand.logo_change') : t('brand.logo_upload')}{required && !value ? ' *' : ''}</button>
          {value && !required && <button type="button" onClick={() => onChange(null)} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-rose-600 hover:bg-rose-500/10"><Trash2 className="size-3.5" /> {t('brand.logo_remove')}</button>}
        </div>
        <div className="text-[11px] text-muted-foreground">{t('brand.logo_hint')}</div>
        {err && <div className="text-[11px] text-rose-600">{err}</div>}
      </div>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
}
